import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, map, of, take } from 'rxjs';

import { KlConfirmDialogComponent } from '@shared/components/kl-confirm-dialog/kl-confirm-dialog';

type ConfirmSeverity = 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'help' | 'danger' | 'contrast';

export type KlConfirmTone = 'info' | 'warning' | 'danger';

export interface KlConfirmDialogOpenOptions {
  /** i18n key or literal for the heading inside the body area. */
  title: string;
  /** i18n key or literal for body; omit or empty to hide. */
  description?: string;
  /** i18n key for the DynamicDialog header bar (defaults to the tone's header: `'info'` or `'warning'`). */
  headerLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  icon?: string;
  confirmSeverity?: ConfirmSeverity;
  cancelSeverity?: ConfirmSeverity;
  width?: string;
  closable?: boolean;
  dismissableMask?: boolean;
  closeOnEscape?: boolean;
  confirmLoading?: boolean;
  /** When true, only the primary action is shown (e.g. informational ack dialogs). */
  hideCancel?: boolean;
  /** When true, applies backdrop blur on the modal mask. */
  blurMask?: boolean;
  /** Kind of message; header, icon and button come from TONE_PRESETS. Use 'danger' only for irreversible actions (the default). */
  tone?: KlConfirmTone;
}

/** Header, icon and confirm button defaults per tone; explicit options still override them. */
const TONE_PRESETS = {
  info: { headerLabel: 'info', icon: 'ph ph-info', confirmSeverity: 'primary' },
  warning: { headerLabel: 'warning', icon: 'ph ph-warning', confirmSeverity: 'warn' },
  danger: { headerLabel: 'warning', icon: 'ph ph-warning', confirmSeverity: 'danger' },
} satisfies Record<KlConfirmTone, Pick<KlConfirmDialogOpenOptions, 'headerLabel' | 'icon' | 'confirmSeverity'>>;

/**
 * Opens `KlConfirmDialogComponent` via PrimeNG `DialogService`.
 * Emits `true` when the user confirms, `false` otherwise (cancel, dismiss, or close).
 */
@Injectable({ providedIn: 'root' })
export class KlConfirmDialogService {
  private readonly dialog = inject(DialogService);
  private readonly translate = inject(TranslateService);

  open(options: KlConfirmDialogOpenOptions): Observable<boolean> {
    const ref = this.openDialog(options);
    if (!ref) {
      return of(false);
    }
    return ref.onClose.pipe(
      take(1),
      map((v) => v === true)
    );
  }

  /** Like `open()`, but with a checkbox (e.g. "also delete related record?"). */
  openWithCheckbox(
    options: KlConfirmDialogOpenOptions & { checkboxLabel: string }
  ): Observable<{ confirmed: boolean; checked: boolean }> {
    const ref = this.openDialog(options, { checkboxLabel: options.checkboxLabel });
    if (!ref) {
      return of({ confirmed: false, checked: false });
    }
    return ref.onClose.pipe(
      take(1),
      map((v) =>
        v && typeof v === 'object'
          ? (v as { confirmed: boolean; checked: boolean })
          : { confirmed: false, checked: false }
      )
    );
  }

  private openDialog(options: KlConfirmDialogOpenOptions, extraInputValues?: { checkboxLabel?: string }) {
    const tone = options.tone ?? 'danger';
    const preset = TONE_PRESETS[tone];
    const headerText = this.translate.instant(options.headerLabel ?? preset.headerLabel) as string;
    const shellClass = options.blurMask
      ? 'kl-confirm-dialog-shell kl-confirm-dialog-shell--blur-backdrop'
      : 'kl-confirm-dialog-shell';

    return this.dialog.open(KlConfirmDialogComponent, {
      showHeader: true,
      header: headerText,
      width: options.width ?? 'min(420px, 92vw)',
      modal: true,
      closable: options.closable ?? true,
      dismissableMask: options.dismissableMask ?? true,
      closeOnEscape: options.closeOnEscape ?? true,
      styleClass: shellClass,
      inputValues: {
        title: options.title,
        description: options.description ?? '',
        confirmLabel: options.confirmLabel ?? 'yes',
        cancelLabel: options.cancelLabel ?? 'no',
        icon: options.icon ?? preset.icon,
        confirmSeverity: options.confirmSeverity ?? preset.confirmSeverity,
        tone,
        cancelSeverity: options.cancelSeverity ?? 'secondary',
        confirmLoading: options.confirmLoading ?? false,
        hideCancel: options.hideCancel ?? false,
        ...extraInputValues,
      },
    });
  }
}
